import { useQuery } from '@tanstack/react-query';
import {
  CheckoutCustomer,
  CreateSplitPaymentsGroupPayload,
} from '../../types/payments';
import {
  createSplitPaymentsGroup,
  updateCustomerBeforePayment,
} from '../../services/payments';
import useUser from '../global/useUser';

type Args = Omit<
  CreateSplitPaymentsGroupPayload,
  'businessId' | 'customerId'
> & {
  customer: CheckoutCustomer;
  guest?: boolean;
};
type Options = {
  onSuccess?: (
    result: Awaited<ReturnType<typeof createSplitPaymentsGroup>>,
  ) => void;
  isEnabled?: boolean;
};

function useCreateSplitPaymentsGroup(
  { customer, appointment, discountId, order, guest = false }: Args,
  { onSuccess, isEnabled = true }: Options = {},
) {
  const { user } = useUser();
  const businessId = user?.business?.id;

  return useQuery({
    queryKey: [
      'create-split-payments-group',
      customer,
      appointment,
      discountId,
      order,
      businessId,
      guest,
    ],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async () => {
      const customerId = guest
        ? null
        : await updateCustomerBeforePayment(customer);
      if (!guest && !customerId) {
        throw new Error('Failed to create customer');
      }

      const result = await createSplitPaymentsGroup({
        customerId,
        appointment: appointment || undefined,
        order: order || undefined,
        discountId,
        businessId: businessId!,
      });
      onSuccess?.(result);
      return result;
    },
    enabled:
      isEnabled &&
      (guest || !!customer?.customerId || !!customer?.phoneNumber) &&
      (!!appointment?.appointmentId || !!order?.orderItems?.length),
  });
}

export default useCreateSplitPaymentsGroup;
